// ============================================================================
// PairsForm - Hook compteur de messages non lus
// ============================================================================
// Refonte FX 2026-10-13 (lot 6 messagerie) :
//   - « non lu » = message postérieur à la DERNIÈRE OUVERTURE du fil par la
//     personne (table chat_lectures, POST /chat/lu) — plus « message de moins
//     de 24 h » ;
//   - UN SEUL poller partagé pour le compteur global : trois consommateurs
//     (menu, navigation mobile, hook de navigation) lançaient trois polls ;
//   - `signalerFilLu(groupeId)` : la ChatBox l'appelle quand un fil vient
//     d'être lu — les badges se rafraîchissent sans attendre le prochain poll.
// ============================================================================

import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const INTERVALLE_MS = 30000;
const EVENEMENT_LU = "pairsform:chat-lu";

// ---- Poller global partagé --------------------------------------------------
let compteurGlobal = 0;
const abonnes = new Set<(n: number) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let enCours = false;

async function rafraichirGlobal(): Promise<void> {
  if (enCours) return;
  enCours = true;
  try {
    const res = await api.get<{ count: number }>("/chat/unread-count");
    compteurGlobal = res.data.count || 0;
    abonnes.forEach((f) => f(compteurGlobal));
  } catch {
    // Silencieux
  } finally {
    enCours = false;
  }
}

function abonner(f: (n: number) => void): () => void {
  abonnes.add(f);
  f(compteurGlobal);
  if (!timer) {
    void rafraichirGlobal();
    timer = setInterval(() => void rafraichirGlobal(), INTERVALLE_MS);
  }
  return () => {
    abonnes.delete(f);
    if (abonnes.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/** Un fil vient d'être lu (ChatBox) : badges global et de groupe à jour. */
export function signalerFilLu(groupeId: string): void {
  void rafraichirGlobal();
  window.dispatchEvent(new CustomEvent(EVENEMENT_LU, { detail: { groupeId } }));
}

/**
 * Nombre total de messages non lus dans mes fils (participant ou équipe).
 * Un seul poll partagé entre tous les consommateurs.
 */
export function useUnreadMessages(): number {
  const { user } = useAuth();
  const [count, setCount] = useState(compteurGlobal);

  useEffect(() => {
    if (!user) {
      setCount(0);
      return;
    }
    return abonner(setCount);
  }, [user]);

  return count;
}

/**
 * Variante scopée à UN groupe (recette FX 2026-10-01) : badge de non-lus
 * sur l'onglet Communication / « Mes échanges » d'un groupe. Ne fetch RIEN
 * tant que le groupe n'est pas chargé (un repli silencieux sur le compteur
 * global afficherait un badge faux).
 */
export function useUnreadMessagesGroupe(groupeId: string | undefined): number {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!user || !groupeId) {
      setCount(0);
      return;
    }

    const fetchCount = async () => {
      try {
        const res = await api.get<{ count: number }>("/chat/unread-count", { params: { groupeId } });
        setCount(res.data.count || 0);
      } catch {
        // Silencieux
      }
    };
    const surLecture = (e: Event) => {
      const detail = (e as CustomEvent<{ groupeId?: string }>).detail;
      if (detail?.groupeId === groupeId) setCount(0);
    };

    fetchCount();
    pollRef.current = setInterval(fetchCount, INTERVALLE_MS);
    window.addEventListener(EVENEMENT_LU, surLecture);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener(EVENEMENT_LU, surLecture);
    };
  }, [user, groupeId]);

  return count;
}
